/**
 * Website Feed — everything needed to wire the external Faith Works Bookshop
 * site to this shop, plus proof that it is pulling live data.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, Copy, Radio } from 'lucide-react';

const copy = (text, what) => {
  navigator.clipboard?.writeText(text);
  toast.success(`${what} copied`);
};

const when = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return then.toLocaleDateString();
};

export const ShopWebsiteFeedTab = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-website-feed'],
    queryFn: async () => (await api.get('/shop/admin/website-feed')).data,
    refetchInterval: 30000,
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-5" data-testid="shop-website-feed">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Live on the website</CardTitle>
          <CardDescription>
            The bookshop site at faithworldministries.com reads this shop directly.
            Publish here, it shows there — no re-upload, no second catalogue.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[['Products served', data?.products_published ?? 0, 'feed-stat-products'],
            ['Categories live', data?.categories_live ?? 0, 'feed-stat-categories'],
            ['Website orders', data?.website_orders ?? 0, 'feed-stat-orders'],
            ['Last pull', when(data?.last_pull_at), 'feed-stat-lastpull']].map(
            ([label, value, testId]) => (
              <div key={label}>
                <p className="text-xs text-slate-500">{label}</p>
                <p className="mt-1.5 text-xl font-semibold" data-testid={testId}>{value}</p>
              </div>
            ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Paste this on the website</CardTitle>
          <CardDescription>
            One script tag plus the slots below. Everything else is handled here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-lg
                          overflow-x-auto whitespace-pre-wrap"
               data-testid="feed-snippet">{data?.snippet || ''}</pre>
          <Button size="sm" variant="outline" data-testid="feed-copy-snippet"
                  onClick={() => copy(data?.snippet || '', 'Snippet')}>
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy snippet
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Feed endpoints</CardTitle>
          <CardDescription>
            Each row shows what it serves right now and when the website last asked for it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data?.endpoints || []).map((e) => (
            <div key={e.path}
                 className="flex flex-wrap items-center gap-3 border-b border-slate-100 pb-3"
                 data-testid={`feed-endpoint-${e.label.split(' ')[0].toLowerCase()}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-sm">{e.label}</span>
                  {e.pulls > 0 ? (
                    <Badge variant="outline"
                           className="text-[10px] border-emerald-200 text-emerald-700">
                      <Check className="h-3 w-3 mr-1" /> pulled {e.pulls}×
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-slate-500">
                      <Radio className="h-3 w-3 mr-1" /> waiting for first pull
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500 font-mono break-all">{e.url}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {e.note} · serving {e.serves} · last pull {when(e.last_pull_at)}
                  {e.last_origin ? ` from ${e.last_origin}` : ''}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copy(e.url, 'URL')}
                      data-testid={`feed-copy-${e.label.split(' ')[0].toLowerCase()}`}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Latest orders from the website</CardTitle>
          <CardDescription>
            Checkouts on the external site land straight in Orders here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data?.recent_website_orders || []).length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="feed-orders-empty">
              No orders from the website yet.
            </p>
          ) : data.recent_website_orders.map((o) => (
            <p key={o.order_number} className="flex flex-wrap justify-between text-sm"
               data-testid={`feed-order-${o.order_number}`}>
              <span className="text-slate-600">
                <span className="font-mono text-xs text-slate-400">{o.order_number}</span>
                {' '}{o.customer}
              </span>
              <span className="font-medium">
                {o.currency === 'USD' ? '$' : `${o.currency} `}
                {Number(o.total || 0).toFixed(2)} · {o.status} · {when(o.at)}
              </span>
            </p>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopWebsiteFeedTab;
